import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type Stats } from "../api";
import { useProjects } from "../project-context";

const RANGES = ["24h", "7d", "30d", "90d"] as const;
type Range = (typeof RANGES)[number];

function flag(cc: string): string {
  if (!cc || cc.length !== 2 || cc === "??") return "🏳";
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function fmt(n: number): string {
  return new Intl.NumberFormat("nb-NO").format(n);
}

function BarList({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: number; lead?: string }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="card reveal">
      <h3 className="card-title">{title}</h3>
      {rows.length === 0 ? (
        <div className="empty">ingen data ennå</div>
      ) : (
        <div className="bars">
          {rows.map((r, i) => (
            <div className="bar-row" key={i}>
              <span className="fill" style={{ width: `${(r.value / max) * 100}%` }} />
              <span className="label">
                {r.lead && <span className="flag">{r.lead}</span>}
                {r.label}
              </span>
              <span className="num">{fmt(r.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Overview() {
  const { selected, loading: projLoading } = useProjects();
  const [range, setRange] = useState<Range>("7d");
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api
      .stats(selected.id, range)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [selected, range]);

  if (projLoading) return <div className="spin" />;

  if (!selected) {
    return (
      <div className="card reveal" style={{ marginTop: 40 }}>
        <h3 className="card-title">Ingen prosjekter ennå</h3>
        <p style={{ color: "var(--muted)", fontSize: 14 }}>
          Opprett ditt første prosjekt under <strong>Prosjekter</strong> for å få en sporingsnøkkel.
        </p>
      </div>
    );
  }

  const ts = stats?.timeseries ?? [];

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">{selected.name}</span>
          <h1 className="page-title">Oversikt</h1>
        </div>
        <div className="segment">
          {RANGES.map((r) => (
            <button key={r} className={r === range ? "on" : ""} onClick={() => setRange(r)}>
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-stats" style={{ marginBottom: 16 }}>
        <div className="card stat reveal">
          <div className="eyebrow">Besøkende</div>
          <div className="value">{fmt(stats?.summary.visitors ?? 0)}</div>
          <div className="delta">unike · cookieless</div>
        </div>
        <div className="card stat reveal" style={{ animationDelay: "60ms" }}>
          <div className="eyebrow">Sidevisninger</div>
          <div className="value">{fmt(stats?.summary.pageviews ?? 0)}</div>
          <div className="delta">totalt i perioden</div>
        </div>
        <div className="card stat reveal" style={{ animationDelay: "120ms" }}>
          <div className="eyebrow">Sider / besøk</div>
          <div className="value">
            {stats && stats.summary.visitors > 0
              ? (stats.summary.pageviews / stats.summary.visitors).toFixed(1)
              : "0.0"}
          </div>
          <div className="delta">snitt dybde</div>
        </div>
      </div>

      <div className="card reveal" style={{ marginBottom: 16, animationDelay: "120ms" }}>
        <h3 className="card-title">
          Trafikk
          {loading && <span className="mono" style={{ fontSize: 11, color: "var(--faint)" }}>oppdaterer …</span>}
        </h3>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={ts} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
            <defs>
              <linearGradient id="gLime" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#45b793" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#45b793" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(128,134,148,0.16)" vertical={false} />
            <XAxis
              dataKey="t"
              tick={{ fill: "#8a909d", fontSize: 11, fontFamily: "JetBrains Mono" }}
              tickFormatter={(v: string) =>
                range === "24h"
                  ? new Date(v).toLocaleTimeString("nb-NO", { hour: "2-digit" })
                  : new Date(v).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit" })
              }
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#8a909d", fontSize: 11, fontFamily: "JetBrains Mono" }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={42}
            />
            <Tooltip
              contentStyle={{
                background: "var(--panel)",
                border: "1px solid var(--line-strong)",
                borderRadius: 10,
                color: "var(--text)",
                fontFamily: "JetBrains Mono",
                fontSize: 12,
              }}
              labelStyle={{ color: "var(--muted)" }}
              labelFormatter={(v) => new Date(v as string).toLocaleString("nb-NO")}
            />
            <Area
              type="monotone"
              dataKey="visitors"
              name="Besøkende"
              stroke="#45b793"
              strokeWidth={2}
              fill="url(#gLime)"
            />
            <Area
              type="monotone"
              dataKey="pageviews"
              name="Sidevisninger"
              stroke="#6f96d8"
              strokeWidth={1.5}
              fillOpacity={0}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-2">
        <BarList
          title="Topp sider"
          rows={(stats?.topPages ?? []).map((r) => ({ label: r.path, value: r.views }))}
        />
        <BarList
          title="Kilder"
          rows={(stats?.topSources ?? []).map((r) => ({ label: r.source, value: r.visitors }))}
        />
        <BarList
          title="Land"
          rows={(stats?.topCountries ?? []).map((r) => ({
            label: r.country === "??" ? "Ukjent" : r.country,
            value: r.visitors,
            lead: flag(r.country),
          }))}
        />
        <BarList
          title="Enheter &amp; nettlesere"
          rows={[
            ...(stats?.devices ?? []).map((r) => ({ label: r.device, value: r.visitors })),
            ...(stats?.browsers ?? []).map((r) => ({ label: r.browser, value: r.visitors })),
          ]}
        />
      </div>

      {(stats?.topEvents.length ?? 0) > 0 && (
        <div style={{ marginTop: 16 }}>
          <BarList
            title="Egendefinerte events"
            rows={(stats?.topEvents ?? []).map((r) => ({ label: r.name, value: r.count }))}
          />
        </div>
      )}

      <p className="footer-note">
        Cookieless · IP lagres aldri · Geo-data fra{" "}
        <a href="https://db-ip.com" target="_blank" rel="noreferrer">DB-IP</a> (CC BY 4.0)
      </p>
    </div>
  );
}
